// Stillhouse — persistent degraded-mode banner (COM-94).
//
// The control plane boots without a reachable DATABASE_URL. The API logs one
// warning line and carries on; the admin gets a control plane that loads fine
// and shows an empty fleet, which reads as "the product is broken" rather than
// "the database is not connected". A log line on the server is invisible to
// someone looking at a browser, so the same state is surfaced here.
//
// Mounted above every view in App.tsx, including the login screen: a dead
// database also blocks sign-in, and a bare "500 Internal Server Error" under
// the password field explains nothing.
//
// Signal precedence:
//   1. GET /api/health -> `database` (authoritative, self-healing — the banner
//      disappears on its own once the DB comes back).
//   2. Any 5xx seen by the API client, used only when health reports no
//      `database` field at all (older API). Sticky until reload.
import React, { useEffect, useState } from "react";
import { C, F } from "../brand/tokens";
import { api, getLastServerError, onServerError, type HealthDatabase } from "../api";

const POLL_MS = 20_000;

type Severity = "warn" | "danger";

interface Notice {
  severity: Severity;
  /** Short mono kicker, e.g. DATABASE UNREACHABLE. */
  kicker: string;
  /** What the admin is actually looking at. */
  body: string;
  /** The one thing to do about it. */
  fix: string;
  /** Raw operator detail (connection error), rendered mono when present. */
  detail?: string | null;
}

// Map the health payload to what the admin needs to read. Returns null when the
// database is fine — the banner is not a permanent chrome element.
function noticeFor(db: HealthDatabase): Notice | null {
  if (db.configured && db.reachable) return null;
  if (!db.configured) {
    return {
      severity: "warn",
      kicker: "DATABASE NOT CONFIGURED",
      body:
        "DATABASE_URL is unset, so the control plane is running in degraded mode. An empty fleet below means no data can be read — not that there are no machines. Enrolment, sessions and the telemetry poller are all disabled.",
      // `pnpm run setup`, not `pnpm setup` — the latter is pnpm's own built-in
      // environment command and never reaches the package script.
      fix: "Copy apps/api/.env.example to apps/api/.env, start Postgres (docker compose up -d), then run pnpm run setup.",
    };
  }
  return {
    severity: "danger",
    kicker: "DATABASE UNREACHABLE",
    body:
      "DATABASE_URL is set but the database did not answer, so the control plane is running in degraded mode. An empty fleet below means no data can be read — not that there are no machines.",
    fix: "Check the database is up (docker compose up -d) and that DATABASE_URL points at it.",
    detail: db.error ?? null,
  };
}

// Used only when the API serves no `database` field: a 5xx is the strongest
// evidence available, and DB-down is its most common cause here.
function noticeForServerError(msg: string): Notice {
  return {
    severity: "danger",
    kicker: "BACKEND ERRORS",
    body:
      "The API returned a server error, so what this page shows is incomplete. An empty fleet below may mean the control plane cannot reach its database — not that there are no machines.",
    fix: "Check the API logs, then confirm the database is up and DATABASE_URL is correct.",
    detail: msg,
  };
}

export function DegradedBanner() {
  const [notice, setNotice] = useState<Notice | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let alive = true;
    // Only consulted while health reports no `database`; kept in a ref-like
    // closure variable so a later poll can still overrule it.
    let fallback: string | null = getLastServerError();

    const apply = (db: HealthDatabase | undefined) => {
      if (!alive) return;
      if (db) setNotice(noticeFor(db));
      else setNotice(fallback ? noticeForServerError(fallback) : null);
    };

    let lastDb: HealthDatabase | undefined;
    const poll = () =>
      api
        .health()
        .then((h) => {
          lastDb = h.database;
          apply(h.database);
        })
        .catch(() => {
          // Health itself is unreachable — the API is down, which is a louder
          // problem than a degraded DB but not one this banner should guess
          // about beyond what it already saw.
          if (!alive) return;
          lastDb = undefined;
          apply(undefined);
        });

    void poll();
    const timer = setInterval(() => void poll(), POLL_MS);
    const unsubscribe = onServerError((msg) => {
      fallback = msg;
      apply(lastDb);
    });

    return () => {
      alive = false;
      clearInterval(timer);
      unsubscribe();
    };
  }, []);

  if (!notice) return null;

  const accent = notice.severity === "danger" ? C.rose : C.amber;

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        flexShrink: 0,
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "10px 22px",
        background: `${accent}14`,
        borderBottom: `1px solid ${accent}55`,
        color: C.fgDark,
        fontFamily: F.ui,
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <span
        aria-hidden
        style={{
          flexShrink: 0,
          marginTop: 2,
          width: 18,
          height: 18,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: accent,
          color: C.ink,
          fontFamily: F.mono,
          fontSize: 12,
          fontWeight: 800,
        }}
      >
        !
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontFamily: F.mono,
            fontSize: 10,
            letterSpacing: "0.2em",
            fontWeight: 700,
            color: accent,
          }}
        >
          {notice.kicker} · DEGRADED MODE
        </div>
        <div style={{ marginTop: 4, color: C.fgDark }}>{notice.body}</div>
        {expanded && (
          <React.Fragment>
            <div style={{ marginTop: 6, color: C.fgMute }}>{notice.fix}</div>
            {notice.detail && (
              <div
                style={{
                  marginTop: 6,
                  fontFamily: F.mono,
                  fontSize: 11,
                  color: C.fgFaint,
                  wordBreak: "break-word",
                }}
              >
                {notice.detail}
              </div>
            )}
          </React.Fragment>
        )}
      </div>
      {/* Collapsible, never dismissible: the state it reports does not go away
          because the admin acknowledged it. */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        style={{
          flexShrink: 0,
          background: "transparent",
          border: `1px solid ${accent}55`,
          color: accent,
          cursor: "pointer",
          padding: "4px 10px",
          fontFamily: F.mono,
          fontSize: 10,
          letterSpacing: "0.16em",
          fontWeight: 700,
        }}
      >
        {expanded ? "LESS" : "HOW TO FIX"}
      </button>
    </div>
  );
}
